import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MergeTypeIcon from '@mui/icons-material/MergeType';
import PublicIcon from '@mui/icons-material/Public';
import {
  Box,
  Button,
  ButtonGroup,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate } from "react-router-dom";
import ConfirmationDialog from "../components/ConfirmationDialog";
import CustomDialog from "../components/CustomDialog";
import LazyLoader from "../components/LazyLoader";
import MergeList from "../components/MergeList";
import SearchField from "../components/SearchField";
import useSnackbar from "../components/snackbar/useSnackBar";
import { useApi } from "../hooks/useApi";
import { useLanguage } from "../hooks/useLanguage";
import { getAttributeIcon, getUnitLabel } from "../utils/lib";
import EditAttribute from "./EditAttribute";

/**
 * Show list of attributes according to the specified filter.
 * @param {*} param0 
 * @returns 
 */
export default function Attribute({ userInfo, publicFlag, allFlag, changeTab, trigger }) {
  const [attributes, setAttributes] = useState([]);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [endReached, setEndReached] = useState(false);
  const [query, setQuery] = useState("");

  // counter to trigger updates
  const [updateCounter, setUpdateCounter] = useState(1);

  // state for deletion-dialog
  const [isOpen, setIsOpen] = useState({ state: false, id: null });

  // state for merge-dialog
  const [isOpenMerge, setIsOpenMerge] = useState(null);

  // state for editing-dialog
  const [isOpenEdit, setIsOpenEdit] = useState(null);

  const intl = useIntl();

  const api = useApi();

  const { locale } = useLanguage();

  const navigate = useNavigate();

  /**
   * Whenever the page is changed, fetch new data (except first page).
   */
  useEffect(() => {
    // do not fetch the first page here
    if (page === 1 || endReached) return;
    fetchPage(page, query);
  }, [page]);

  /**
   * Start new fetch from page 1.
   */
  useEffect(() => {
    setPage(1);
    setEndReached(false);
    fetchPage(1, query);
  }, [updateCounter, locale, query, trigger]);

  /**
   * Fetches the specified page with query.
   * @param {*} page 
   * @param {*} query
   */
  const fetchPage = (page, query) => {
    // TODO: right now uses country=DE as default region, should be changed in the future to reflect current region
    let fetchURL = `/attribute?page=${page - 1}&size=20&published=${publicFlag}&all=${allFlag}&usage=true&addInfo=true&country=DE&lang=${locale}`;
    if (query) {
      fetchURL += `&search=${query}`;
    }
    setIsLoading(true);
    api
      .get(fetchURL)
      .then(response => {
        if (page === 1) {
          setAttributes(response.data.content);
        } else {
          setAttributes(old => [...old, ...response.data.content]);
        }
        setEndReached(response.data.last);
      })
      .catch(error => {
        console.log(error);
        if (error?.response?.status === 403) {
          navigate('/login');
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  const handleSearch = (value) => {
    setQuery(value);
  };

  // open deletion-dialog
  const handleOpen = (id) => {
    setIsOpen({ state: true, id: id });
  };

  // close deletion-dialog
  const handleClose = () => {
    setIsOpen({ state: false, id: null });
  };

  /**
   * Open the merge-dialog.
   * @param {*} id 
   * @param {*} name 
   */
  const handleOpenMerge = (attribute) => {
    setIsOpenMerge(attribute);
  };

  /**
   * Close the merge-dialog.
   */
  const handleCloseMerge = () => {
    setIsOpenMerge(null);
  }

  /**
   * Close the edit-dialog.
   * @param {*} attribute 
   */
  const handleCloseEdit = (attribute) => {
    if (attribute) {
      // creating attribute was successful
      // trigger reload
      setUpdateCounter(prev => prev + 1);
    }
    setIsOpenEdit(null);
  };

  // snackbar for when deletion is not successful
  const { openSnackBar, closeSnackBar } = useSnackbar();

  /**
   * Deletes the specified attribute.
   * @param {*} id 
   */
  const handleDelete = (id) => {
    api
      .delete(`/attribute/${id}`)
      .then((response) => {
        // handle the response
        handleClose();
        setUpdateCounter(prev => prev + 1);
      })
      .catch((error) => {
        // handle errors
        console.log(error);
        handleClose();
        openSnackBar(intl.formatMessage({ id: "Overview.deletionFailure" }));
      });
  }

  /**
   * Makes the specified attribute public and goes to the public-page.
   * @param {*} id 
   */
  const handleMakePublic = (id) => {
    api
      .put(`/attribute/publish/${id}`)
      .then(response => {
        changeTab(1);
      })
      .catch(error => {
        console.log(error);
        openSnackBar(intl.formatMessage({ id: "Attributes.publishFailure" }));
      })
  };

  /**
   * Merges the specified attributes and reloads the attributes.
   * @param {*} aid 
   * @param {*} bid 
   */
  const handleMerge = (aid, bid) => {
    api
      .put(`/attribute/merge/${aid}/${bid}`)
      .then(response => {
        setUpdateCounter(old => old + 1);
      })
      .catch(error => {
        console.log(error);
        openSnackBar(intl.formatMessage({ id: "Attributes.mergeFailure" }));
      })
      .finally(() => {
        handleCloseMerge();
      });
  };

  // dialog that prompts deletion
  const dialog = useMemo(() => {
    let d = (
      <ConfirmationDialog
        title={intl.formatMessage({ id: "Attributes.deletionQuestion" })}
        content={intl.formatMessage({ id: "Overview.deletionInfo" })}
        isOpen={isOpen.state}
        handleAccept={() => handleDelete(isOpen.id)}
        handleClose={handleClose}
      />
    );
    if (isOpen.state) {
      return d;
    }
  }, [isOpen]);

  /**
   * Dialog that prompts merging attributes.
   */
  const mergeDialog = useMemo(() => {
    if (!isOpenMerge) return;
    return <MergeList
      title={isOpenMerge.key + " " + intl.formatMessage({ id: "Attributes.mergeWith" })}
      isOpen={isOpenMerge !== null}
      handleClose={handleCloseMerge}
      handleAccept={(value) => handleMerge(isOpenMerge.id, value)}
      initialID={isOpenMerge.id}
      optionsURL={`/attribute?published=true&all=false&type=${isOpenMerge.type}&filterFamily=true${isOpenMerge.unitFamily ? `&unitFamily=${isOpenMerge.unitFamily}` : ''}`}
      fieldName="key"
    />;
  }, [isOpenMerge]);

  // list of attributes
  const attributeList = useMemo(() => {
    return attributes.map((attribute) => {
      return (
        <TableRow
          key={attribute.id}
          sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
        >
          <TableCell component="th" scope="row" sx={{
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          }} >
            {attribute.key}
          </TableCell>
          <TableCell component="th" scope="row">
            {getAttributeIcon(attribute.type)}
          </TableCell>
          <TableCell component="th" scope="row">
            {getUnitLabel(attribute?.validation?.DE?.unit, attribute?.unitFamily)}
          </TableCell>
          <TableCell component="th" scope="row">
            {attribute.usage}
          </TableCell>
          <TableCell align="right">
            <ButtonGroup
              variant="contained"
              aria-label="outlined primary button group"
            >
              {userInfo?.isAdmin && publicFlag && !allFlag && <Button
                component={Link}
                color="yellow"
                aria-label="merge"
                onClick={() => handleOpenMerge(attribute)}
              >
                <MergeTypeIcon />
              </Button>}
              {userInfo?.isAdmin && !publicFlag && <Button
                component={Link}
                color="yellow"
                aria-label="make public"
                onClick={() => handleMakePublic(attribute.id)}
              >
                <PublicIcon />
              </Button>}
              {(userInfo?.isAdmin || attribute.ownerId === userInfo?.ownerId) && <Button
                component={Link}
                color="primary"
                aria-label="edit"
                onClick={() => setIsOpenEdit(attribute)}
              >
                <EditIcon />
              </Button>}
              {(userInfo?.isAdmin || attribute.ownerId === userInfo?.ownerId) && <Button
                component={Link}
                onClick={() => handleOpen(attribute.id)}
                color="error"
                aria-label="delete"
              >
                <DeleteIcon />
              </Button>}
            </ButtonGroup>
          </TableCell>
        </TableRow>
      );
    });
  }, [attributes])

  return (
    <Box>
      <SearchField onChange={(value) => handleSearch(value)} />
      <Box sx={{ width: "100%", height: "100%", margin: 0, padding: 0, marginTop: 1 }}>
        <LazyLoader
          onLoad={() => setPage(prev => prev + 1)}
          loading={isLoading}
          endReached={endReached}
        >
          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table sx={{}} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell><FormattedMessage id="Attributes.name" /></TableCell>
                  <TableCell><FormattedMessage id="Attributes.type" /></TableCell>
                  <TableCell><FormattedMessage id="Attributes.unit" /></TableCell>
                  <TableCell><FormattedMessage id="Attributes.usage" /></TableCell>
                  <TableCell align="right"><FormattedMessage id="Overview.actions" /></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{attributeList}</TableBody>
            </Table>
          </TableContainer>
        </LazyLoader>
      </Box>

      {dialog}
      {mergeDialog}
      <CustomDialog
        isOpen={isOpenEdit ? true : false}
        handleClose={() => setIsOpenEdit(null)}
        title={intl.formatMessage({ id: "Attributes.edit" })}
        content={<EditAttribute userInfo={userInfo} id={isOpenEdit?.id} onClose={handleCloseEdit} />}
        maxWidth="xl"
        hideConfirmation={true}
        displayClose={true}
      />
    </Box>
  );
};